import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useAdmin } from '../../contexts/AdminContext';
import {
  Key,
  Copy,
  Trash2,
  Eye,
  EyeOff,
  Code2,
  AlertTriangle,
  ArrowRight,
} from 'lucide-react';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;

/**
 * Gerenciamento da chave da API Pública de Áudio (gerar / exibir / revogar).
 * Movido da página Meu Plano para a página de API (/api-docs) em 2026-06.
 * Endpoints INTOCADOS: POST/GET/DELETE /api/audio-api/keys.
 */
const AudioApiKeySection = ({ onKeysChanged }) => {
  const { t } = useTranslation();
  const { subscriptionData, isSuperAdmin } = useAdmin();

  const planType = subscriptionData?.plan_type?.toLowerCase() || '';
  const isTrial = planType === 'trial';
  const hasPaidPlan = subscriptionData?.has_subscription && !isTrial && !isSuperAdmin;
  const hasApiAccess = ['pro', 'plus', 'master'].includes(planType) || isSuperAdmin;

  const [apiKeyData, setApiKeyData] = useState(null);
  const [newKeyValue, setNewKeyValue] = useState('');
  const [showKeyModal, setShowKeyModal] = useState(false);
  const [loadingKey, setLoadingKey] = useState(false);
  const [keyVisible, setKeyVisible] = useState(false);

  const fetchApiKey = useCallback(async () => {
    if (!hasApiAccess) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${BACKEND_URL}/api/audio-api/keys`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const keys = res.data?.keys || [];
      const active = keys.find(k => k.is_active);
      setApiKeyData(active || null);
    } catch { /* silent */ }
  }, [hasApiAccess]);

  useEffect(() => { fetchApiKey(); }, [fetchApiKey]);

  const handleGenerateKey = async () => {
    setLoadingKey(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${BACKEND_URL}/api/audio-api/keys`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNewKeyValue(res.data.api_key);
      setApiKeyData(res.data.key_info);
      setShowKeyModal(true);
      toast.success(t('apiKey.generated_toast'));
      onKeysChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('apiKey.generate_error'));
    } finally {
      setLoadingKey(false);
    }
  };

  const handleRevokeKey = async () => {
    if (!apiKeyData?.id) return;
    if (!window.confirm(t('apiKey.revoke_confirm'))) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/audio-api/keys/${apiKeyData.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setApiKeyData(null);
      setNewKeyValue('');
      toast.success(t('apiKey.revoked_toast'));
      onKeysChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('apiKey.revoke_error'));
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success(t('apiKey.copied_key')));
  };

  if (!hasApiAccess || !(hasPaidPlan || isSuperAdmin)) return null;

  return (
    <>
      <div id="minha-chave" className="dp-top-accent" style={{
        background: 'var(--dp-bg-card)',
        border: '1px solid var(--dp-border-subtle)',
        borderRadius: '14px',
        padding: '24px 28px',
        marginBottom: '24px',
        scrollMarginTop: '80px',
        animation: 'dp-fadeInUp 0.45s ease',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: '16px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{
              width: '36px', height: '36px', borderRadius: '10px',
              background: 'linear-gradient(135deg, #0DAEFF 0%, #3B82F6 100%)',
              boxShadow: '0 4px 14px rgba(13,174,255,0.28)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Code2 size={18} color="#fff" />
            </div>
            <div>
              <div style={{
                fontSize: '14px', fontWeight: 600,
                color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)',
              }}>
                {t('apiKey.title')}
              </div>
              <div style={{
                fontSize: '12px', color: 'var(--dp-text-tertiary)',
                fontFamily: 'var(--dp-font)',
              }}>
                {t('apiKey.subtitle')}
              </div>
            </div>
          </div>
          <span style={{
            fontSize: '10px', fontWeight: 600, color: 'var(--dp-blue-500, #0DAEFF)',
            background: 'rgba(13, 174, 255, 0.08)',
            padding: '3px 10px', borderRadius: '9999px',
            border: '1px solid rgba(13, 174, 255, 0.22)',
            fontFamily: 'var(--dp-font)', letterSpacing: '0.5px',
          }}>
            {(isSuperAdmin ? 'admin' : planType).toUpperCase()}
          </span>
        </div>

        {/* Content */}
        {!apiKeyData ? (
          <div style={{ textAlign: 'center', padding: '12px 0' }}>
            <p style={{
              fontSize: '13px', color: 'var(--dp-text-secondary)',
              fontFamily: 'var(--dp-font)', margin: '0 0 16px 0', lineHeight: 1.6,
            }}>
              {t('apiKey.empty_desc')}
            </p>
            <button
              className="dp-btn-cta dp-btn-cta--sm"
              onClick={handleGenerateKey}
              disabled={loadingKey}
            >
              <Key size={14} />
              {loadingKey ? t('apiKey.generating') : t('apiKey.generate')}
              <ArrowRight size={14} />
            </button>
          </div>
        ) : (
          <div>
            {newKeyValue && !showKeyModal && (
              <div style={{
                background: 'rgba(251, 191, 36, 0.08)',
                border: '1px solid rgba(251, 191, 36, 0.20)',
                borderRadius: '10px',
                padding: '12px 16px',
                marginBottom: '12px',
                display: 'flex', alignItems: 'center', gap: '10px',
              }}>
                <AlertTriangle size={16} style={{ color: '#FBBF24', flexShrink: 0 }} />
                <span style={{ flex: 1, fontSize: '12px', color: '#FBBF24', fontFamily: 'var(--dp-font)', lineHeight: 1.5 }}>
                  {t('apiKey.copy_now_warning')}
                </span>
                <button
                  onClick={() => copyToClipboard(newKeyValue)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '5px',
                    padding: '6px 12px', borderRadius: '8px',
                    background: 'rgba(251, 191, 36, 0.15)', border: '1px solid rgba(251, 191, 36, 0.30)',
                    color: '#FBBF24', fontSize: '11px', fontWeight: 600,
                    fontFamily: 'var(--dp-font)', cursor: 'pointer', whiteSpace: 'nowrap',
                  }}
                >
                  <Copy size={12} /> {t('apiKey.copy_key')}
                </button>
              </div>
            )}

            <div style={{
              display: 'flex', alignItems: 'center', gap: '10px',
              background: 'var(--dp-bg-secondary)',
              borderRadius: '10px',
              padding: '14px 16px',
              border: '1px solid var(--dp-border-subtle)',
              marginBottom: '12px',
            }}>
              <Key size={16} style={{ color: 'var(--dp-blue-500, #0DAEFF)', flexShrink: 0 }} />
              <code style={{
                flex: 1, fontSize: '13px',
                color: 'var(--dp-text-primary)',
                fontFamily: 'monospace',
                letterSpacing: '0.5px',
                wordBreak: 'break-all',
              }}>
                {keyVisible && newKeyValue
                  ? newKeyValue
                  : `${apiKeyData.key_prefix}••••••••••••••••`}
              </code>
              <button
                onClick={() => newKeyValue && setKeyVisible(!keyVisible)}
                disabled={!newKeyValue}
                title={newKeyValue ? (keyVisible ? t('apiKey.hide') : t('apiKey.show')) : t('apiKey.full_unavailable')}
                style={{
                  background: 'transparent', border: 'none', padding: '4px',
                  cursor: newKeyValue ? 'pointer' : 'not-allowed',
                  opacity: newKeyValue ? 1 : 0.35,
                }}
              >
                {keyVisible && newKeyValue
                  ? <EyeOff size={15} style={{ color: 'var(--dp-text-tertiary)' }} />
                  : <Eye size={15} style={{ color: 'var(--dp-text-tertiary)' }} />}
              </button>
              <button
                onClick={() => {
                  if (newKeyValue) {
                    copyToClipboard(newKeyValue);
                  } else {
                    navigator.clipboard.writeText(apiKeyData.key_prefix);
                    toast.info(t('apiKey.prefix_only'));
                  }
                }}
                title={t('apiKey.copy_key')}
                style={{
                  background: 'transparent', border: 'none', padding: '4px',
                  cursor: 'pointer',
                }}
              >
                <Copy size={15} style={{ color: 'var(--dp-text-tertiary)' }} />
              </button>
              <span style={{
                fontSize: '11px', color: 'var(--dp-text-tertiary)',
                fontFamily: 'var(--dp-font)', whiteSpace: 'nowrap',
              }}>
                {apiKeyData.last_used_at
                  ? t('apiKey.last_used', { date: new Date(apiKeyData.last_used_at).toLocaleDateString('pt-BR') })
                  : t('apiKey.never_used')}
              </span>
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={handleRevokeKey}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '5px',
                  padding: '7px 14px', borderRadius: '8px',
                  background: 'transparent',
                  border: '1px solid rgba(239, 68, 68, 0.25)',
                  color: '#EF4444',
                  fontSize: '12px', fontWeight: 500,
                  fontFamily: 'var(--dp-font)',
                  cursor: 'pointer',
                }}
              >
                <Trash2 size={12} />
                {t('apiKey.revoke')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ─── Modal: chave criada ─── */}
      {showKeyModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.60)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 9999,
          animation: 'dp-pageFadeIn 0.2s ease',
        }}
          onMouseDown={(e) => { if (e.target === e.currentTarget) { setShowKeyModal(false); setKeyVisible(false); } }}
        >
          <div
            style={{
              background: 'var(--dp-bg-card)',
              border: '1px solid var(--dp-border-subtle)',
              borderRadius: '16px',
              padding: '32px',
              maxWidth: '520px',
              width: '90%',
              animation: 'dp-fadeInUp 0.3s ease',
            }}
          >
            <div style={{
              display: 'flex', alignItems: 'center', gap: '10px',
              marginBottom: '20px',
            }}>
              <div style={{
                width: '40px', height: '40px', borderRadius: '10px',
                background: 'rgba(52, 211, 153, 0.10)',
                border: '1px solid rgba(52, 211, 153, 0.15)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Key size={20} style={{ color: '#34D399' }} />
              </div>
              <div>
                <div style={{
                  fontSize: '16px', fontWeight: 600,
                  color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)',
                }}>
                  {t('apiKey.modal_title')}
                </div>
                <div style={{
                  fontSize: '12px', color: 'var(--dp-text-tertiary)',
                  fontFamily: 'var(--dp-font)',
                }}>
                  {t('apiKey.modal_subtitle')}
                </div>
              </div>
            </div>

            <div style={{
              background: 'var(--dp-bg-secondary)',
              borderRadius: '10px',
              padding: '14px 16px',
              border: '1px solid var(--dp-border-subtle)',
              marginBottom: '16px',
              display: 'flex', alignItems: 'center', gap: '10px',
            }}>
              <code style={{
                flex: 1, fontSize: '13px',
                color: 'var(--dp-text-primary)',
                fontFamily: 'monospace',
                wordBreak: 'break-all',
                lineHeight: 1.5,
              }}>
                {keyVisible ? newKeyValue : newKeyValue.slice(0, 8) + '••••••••••••••••••••••••••••••••••••••••••••'}
              </code>
              <button
                onClick={() => setKeyVisible(!keyVisible)}
                style={{
                  background: 'transparent', border: 'none',
                  color: 'var(--dp-text-tertiary)', cursor: 'pointer', padding: '4px',
                }}
                title={keyVisible ? t('apiKey.hide') : t('apiKey.show')}
              >
                {keyVisible ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>

            <div style={{
              background: 'rgba(251, 191, 36, 0.06)',
              border: '1px solid rgba(251, 191, 36, 0.15)',
              borderRadius: '10px',
              padding: '12px 14px',
              marginBottom: '20px',
              display: 'flex', alignItems: 'flex-start', gap: '10px',
            }}>
              <AlertTriangle size={16} style={{ color: '#FBBF24', flexShrink: 0, marginTop: '1px' }} />
              <span style={{
                fontSize: '12px', color: 'var(--dp-text-secondary)',
                fontFamily: 'var(--dp-font)', lineHeight: 1.5,
              }}>
                {t('apiKey.modal_warning')}
              </span>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                className="dp-btn-cta dp-btn-cta--sm"
                onClick={() => copyToClipboard(newKeyValue)}
                style={{ flex: 1 }}
              >
                <Copy size={14} />
                {t('apiKey.copy_button')}
              </button>
              <button
                onClick={() => { setShowKeyModal(false); setKeyVisible(false); }}
                style={{
                  padding: '11px 18px', borderRadius: '10px',
                  background: 'transparent',
                  border: '1px solid var(--dp-border-medium)',
                  color: 'var(--dp-text-secondary)',
                  fontSize: '13px', fontWeight: 500,
                  fontFamily: 'var(--dp-font)',
                  cursor: 'pointer',
                }}
              >
                {t('apiKey.understood')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AudioApiKeySection;
